import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import api from "../../services/api";
import { Input } from "../ui/input";

function unwrapData(payload) {
  if (payload?.data !== undefined) return payload.data;
  return payload;
}

function GlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults(null);
      setError("");
      setLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError("");
    try {
      const response = await api.get("/admin/search", {
        params: { q: trimmed, limit: 5 },
        signal: controller.signal,
      });
      if (requestId !== requestIdRef.current) return;
      setResults(unwrapData(response.data));
    } catch (err) {
      if (
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED" ||
        controller.signal.aborted
      ) {
        return;
      }
      if (requestId !== requestIdRef.current) return;
      setResults(null);
      setError(
        err.response?.data?.message || "Search failed. Try again."
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      if (abortRef.current) abortRef.current.abort();
      requestIdRef.current += 1;
      setResults(null);
      setLoading(false);
      setError("");
      return;
    }

    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch]);

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const go = (href) => {
    setOpen(false);
    setQuery("");
    setResults(null);
    navigate(href);
  };

  const sections = results
    ? [
        { key: "users", title: "Users", items: results.users || [] },
        { key: "courses", title: "Courses", items: results.courses || [] },
        {
          key: "questions",
          title: "Questions",
          items: results.questions || [],
        },
        { key: "content", title: "CMS content", items: results.content || [] },
      ].filter((s) => s.items.length > 0)
    : [];

  const showPanel =
    open && (loading || error || sections.length > 0 || query.trim().length >= 2);

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search users, courses, questions…"
          className="pl-10"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              e.currentTarget.blur();
            }
          }}
        />
      </div>

      {showPanel && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md">
          {loading && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching…
            </div>
          )}

          {!loading && error && (
            <p className="px-4 py-3 text-sm text-destructive">{error}</p>
          )}

          {!loading && !error && sections.length === 0 && query.trim().length >= 2 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              No results for &ldquo;{query.trim()}&rdquo;
            </p>
          )}

          {!loading &&
            sections.map((section) => (
              <div key={section.key} className="border-b border-border last:border-0">
                <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.title}
                </p>
                <ul>
                  {section.items.map((item) => (
                    <li key={`${section.key}-${item.id}`}>
                      <button
                        type="button"
                        className="flex w-full flex-col items-start px-4 py-2 text-left text-sm hover:bg-accent"
                        onClick={() => go(item.href)}
                      >
                        <span className="font-medium">{item.label}</span>
                        {section.key === "users" && item.email && (
                          <span className="text-xs text-muted-foreground">
                            {item.email}
                          </span>
                        )}
                        {section.key === "content" && item.category && (
                          <span className="text-xs text-muted-foreground">
                            {item.category}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
